import { makeAutoObservable } from "mobx"

import type { DocsPageMetadata } from "../types"

interface MDXModule {
  default: React.ComponentType
  frontmatter?: DocsPageMetadata
}

// Vite glob import - lazy loads all MDX files
// Each file becomes a separate chunk automatically
const docsModules = import.meta.glob<MDXModule>(
  "@/content/docs/**/*.mdx",
  { eager: false }, // Lazy load - code splitting!
)

// Eager import to get frontmatter for all pages (titles, order for index)
const docsModulesEager = import.meta.glob<MDXModule>(
  "@/content/docs/**/*.mdx",
  { eager: true },
)

export class DocsStore {
  private loadedPages = new Map<string, MDXModule["default"]>()
  private metadataCache: DocsPageMetadata[] | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get allPages(): DocsPageMetadata[] {
    if (!this.metadataCache) {
      // Extract metadata from module paths and eagerly loaded frontmatter
      this.metadataCache = Object.keys(docsModules)
        .map((path) => {
          const slug = path.replace(/^.*\/docs\//, "").replace(/\.mdx$/, "")
          const eagerModule = docsModulesEager[path] as MDXModule | undefined
          const frontmatter = eagerModule?.frontmatter

          return {
            slug,
            title: frontmatter?.title ?? slug,
            date: frontmatter?.date,
            description: frontmatter?.description,
            category: frontmatter?.category,
            order: frontmatter?.order,
            path,
          }
        })
        .sort((a, b) => {
          // Sort by order if available, then by slug
          const aOrder = a.order ?? 999
          const bOrder = b.order ?? 999
          if (aOrder !== bOrder) return aOrder - bOrder
          return a.slug.localeCompare(b.slug)
        })
    }
    return this.metadataCache
  }

  getPageBySlug(slug: string): DocsPageMetadata | undefined {
    return this.allPages.find((p) => p.slug === slug)
  }

  async loadPage(slug: string): Promise<{
    Component: React.ComponentType
    frontmatter: DocsPageMetadata
  }> {
    // Check cache
    if (this.loadedPages.has(slug)) {
      const Component = this.loadedPages.get(slug)!
      const metadata = this.getPageBySlug(slug)!
      return { Component, frontmatter: metadata }
    }

    // Find matching module path
    const modulePath = Object.keys(docsModules).find((path) => {
      const pathSlug = path.replace(/^.*\/docs\//, "").replace(/\.mdx$/, "")
      return pathSlug === slug
    })

    if (!modulePath) {
      throw new Error(`Docs page not found: ${slug}`)
    }

    // Vite handles dynamic import and code splitting!
    const module = await docsModules[modulePath]()

    if (!module.default) {
      throw new Error(`Invalid MDX module: ${slug}`)
    }

    // Cache component
    this.loadedPages.set(slug, module.default)

    // Update metadata with frontmatter if available
    const metadata = this.getPageBySlug(slug)!
    if (module.frontmatter) {
      Object.assign(metadata, module.frontmatter)
    }

    return {
      Component: module.default,
      frontmatter: metadata,
    }
  }

  isPageLoaded(slug: string): boolean {
    return this.loadedPages.has(slug)
  }
}
